import { inject } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';
import { SyllabusQuery, SyllabusService } from '@examdojo/category/v2';
import { mapToVoid } from '@examdojo/rxjs';
import { catchError, EMPTY, first, of, tap } from 'rxjs';
import { CmsUrlParts, SYLLABUS_SLUG_PARAM } from '../cms.model';

export const syllabusesResolver: ResolveFn<void> = (route) => {
  const syllabusService = inject(SyllabusService);
  const syllabusQuery = inject(SyllabusQuery);

  const router = inject(Router);

  const syllabusSlugParam = route.paramMap.get(SYLLABUS_SLUG_PARAM);

  if (!syllabusSlugParam) {
    router.navigate([`/${CmsUrlParts.Overview}`]);
    return of(undefined);
  }

  // Check if syllabuses are already loaded to avoid redundant API calls
  const existingSyllabuses = syllabusQuery.getAllEntities();
  if (existingSyllabuses.length > 0) {
    // Syllabuses already loaded, just set the active one
    const targetSyllabus = syllabusQuery.getBySlug(syllabusSlugParam);

    if (!targetSyllabus) {
      router.navigate([`/${CmsUrlParts.Overview}`]);
      return of(undefined);
    }

    // Only set active if it's different from current active
    const currentActive = syllabusQuery.getActive();
    if (!currentActive || currentActive.id !== targetSyllabus.id) {
      syllabusService.setActive(targetSyllabus.id);
    }

    return of(undefined);
  }

  // Syllabuses not loaded yet, fetch them
  return syllabusService.fetchSyllabuses().pipe(
    catchError(() => EMPTY), // error initially handled and rethrown by the service.
    first(),
    tap((syllabuses) => {
      if (!syllabuses.length) {
        return;
      }

      const targetSyllabus = syllabusQuery.getBySlug(syllabusSlugParam);

      if (!targetSyllabus) {
        router.navigate([`/${CmsUrlParts.Overview}`]);
        return;
      }

      syllabusService.setActive(targetSyllabus.id);
    }),
    mapToVoid(),
  );
};
